/**
 * Types pour les tâches - ALIGNÉ AVEC LE SCHÉMA PRISMA BACKEND
 */

// Enum TaskStatus - ALIGNÉ avec Prisma
export enum TaskStatus {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE'
}

// Interface principale Task
export interface Task {
  id: number;
  userId: number;           // Propriétaire de la tâche
  title: string;
  description?: string | null;
  status: TaskStatus;
  dueDate?: string | null;  // ISO string date
  assignedToId?: number | null;
  createdAt: string;        // ISO string date
  updatedAt: string;        // ISO string date
  
  // Relations populées (optionnelles selon le contexte)
  user?: TaskUser;          // Propriétaire
  assignedTo?: TaskUser;    // Assigné à
  comments?: TaskComment[];
  emailAssociations?: EmailTaskAssociation[];
  calendarEventReference?: CalendarEventReference;
}

// Interface pour User dans le contexte Task (évite les imports circulaires)
export interface TaskUser {
  id: number;
  name: string;
  email: string;
  role: string;
}

// Interface TaskComment - ALIGNÉ avec Prisma
export interface TaskComment {
  id: number;
  taskId: number;
  authorId: number;
  content: string;
  createdAt: string;        // ISO string date
  updatedAt: string;        // ISO string date
  
  // Relations populées (optionnelles)
  task?: Task;
  author?: TaskUser;
}

// Interface EmailTaskAssociation - ALIGNÉ avec Prisma
export interface EmailTaskAssociation {
  id: number;
  userId: number;
  emailId: string;          // ID de l'email Gmail
  taskId: number;
  emailSubject?: string | null;
  createdAt: string;        // ISO string date
  
  // Relations populées (optionnelles)
  user?: TaskUser;
  task?: Task;
}

// Interface CalendarEventReference - ALIGNÉ avec Prisma
export interface CalendarEventReference {
  id: number;
  taskId: number;
  googleEventId: string;    // ID de l'événement dans Google Calendar
  calendarId: string;       // ID du calendrier Google
  reminderMinutes: number[]; // Minutes avant l'événement pour les rappels
  lastSynced: string;       // ISO string date
  createdAt: string;        // ISO string date
  updatedAt: string;        // ISO string date
  
  // Relations populées (optionnelles)
  task?: Task;
}

// ============= INTERFACES CRUD =============

// Interface pour créer une tâche
export interface CreateTaskRequest {
  title: string;
  description?: string;
  status?: TaskStatus;      // Par défaut TODO
  dueDate?: string;         // ISO string date
  assignedToId?: number;
}

// Interface pour mettre à jour une tâche
export interface UpdateTaskRequest {
  title?: string;
  description?: string;
  status?: TaskStatus;
  dueDate?: string;         // ISO string date
  assignedToId?: number;
}

// Interface pour créer un commentaire
export interface CreateTaskCommentRequest {
  taskId: number;
  content: string;
}

// Interface pour mettre à jour un commentaire
export interface UpdateTaskCommentRequest {
  content: string;
}

// Interface pour associer un email à une tâche
export interface CreateEmailTaskAssociationRequest {
  emailId: string;
  taskId: number;
  emailSubject?: string;
}

// Interface pour créer une référence calendrier
export interface CreateCalendarEventReferenceRequest {
  taskId: number;
  googleEventId: string;
  calendarId: string;
  reminderMinutes?: number[];
}

// ============= INTERFACES POUR FORMULAIRES =============

// Interface pour les formulaires de création/édition de tâche
export interface TaskFormData {
  title: string;
  description: string;
  status: TaskStatus;
  dueDate: string;          // Date locale pour formulaire
  assignedToId: number | null;
}

// Interface pour les filtres de tâches
export interface TaskFilters {
  status?: TaskStatus;
  assignedToId?: number;
  userId?: number;          // Propriétaire
  dueDateFrom?: string;
  dueDateTo?: string;
  search?: string;          // Recherche dans title/description
}

// Interface pour les statistiques de tâches
export interface TaskStatistics {
  total: number;
  todo: number;
  inProgress: number;
  done: number;
  overdue: number;
  assignedToMe: number;
  createdByMe: number;
}

// ============= TYPES UTILITAIRES =============

// Type pour les options de tri
export type TaskSortField = 'title' | 'status' | 'dueDate' | 'createdAt' | 'updatedAt';
export type SortDirection = 'asc' | 'desc';

export interface TaskSortOptions {
  field: TaskSortField;
  direction: SortDirection;
}

// Type pour les options de pagination
export interface TaskPaginationOptions {
  page: number;
  limit: number;
  filters?: TaskFilters;
  sort?: TaskSortOptions;
}

// Type pour la réponse paginée
export interface TaskPaginatedResponse {
  tasks: Task[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}